import { computed, inject, Injectable, type Signal } from '@angular/core';
import type { Plugin } from '../../models/metadata-response';
import { MetadataService } from '../metadata/metadata';
import { OfflineSessionService } from '../offline/offline-session';

// Offline reading is not a plugin: `config.offlineMode` (per loan) decides it.
const MORE_OPTIONS_FEATURES: Plugin[] = ['tts', 'accessibility', 'languageSelector', 'help'];

/** Features that need the API, hidden while reading offline. */
const ONLINE_ONLY_FEATURES = new Set<Plugin>(['finder', 'languageSelector']);

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  private readonly metadataService = inject(MetadataService);
  private readonly offlineSession = inject(OfflineSessionService);
  private readonly featureSignals = new Map<Plugin, Signal<boolean>>();

  private readonly activeFeatures = computed(() => {
    const features = new Set(this.metadataService.metadata()?.metadata.readerConfig.plugins ?? []);
    // A book with its own narration is not read with the TTS, as in NR1
    if (this.metadataService.hasMediaOverlay()) {
      features.delete('tts');
    }
    return features;
  });

  readonly hasAnyMoreOption = computed(
    () =>
      this.metadataService.isOfflineModeEnabled() ||
      MORE_OPTIONS_FEATURES.some((key) => this.isEnabled(key)()),
  );

  isEnabled(key: Plugin): Signal<boolean> {
    let featureSignal = this.featureSignals.get(key);
    if (!featureSignal) {
      featureSignal = computed(
        () =>
          this.activeFeatures().has(key) &&
          !(ONLINE_ONLY_FEATURES.has(key) && this.offlineSession.isOffline()),
      );
      this.featureSignals.set(key, featureSignal);
    }
    return featureSignal;
  }
}
